﻿import { NavLink } from "react-router-dom";
import useAuth from "../../context/useAuth.js";
import { normalizeRole } from "../../utils/permissions.js";
import { titleCase } from "../../utils/format.js";

/**
 * "The Swastik Professional Salon Sidebar" — DESIGN.md §5: wide, high-contrast (`inverse-surface`),
 * `inverse-on-surface` labels, gold `primary` vertical bar for the active item.
 *
 * Items may carry a `roles` allow-list; omitting it shows the item to everyone.
 * Admins get the full operational set + Staff Management + the financial modules
 * (Payments, Expenses, Reports). Staff get the shop floor: the POS, the
 * appointment book, the client book, the service menu, the product shelf and
 * their own attendance/leaves. Everything money-shaped stays admin-only.
 *
 * Layout: the rail is a full-height flex column of three fixed/scroll bands —
 * brand (fixed) · nav (the only scroll region) · account footer (fixed). Because
 * the footer is a sibling of the scroller rather than the end of it, it stays
 * pinned to the bottom of the viewport on every page, no matter how long the
 * module list or the page content is. The footer is a static identity block
 * (name + role, no avatar) with a direct Sign Out button — it never opens the
 * header account popover.
 */
const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: "dashboard" },
  { to: "/quick-sales", label: "Quick Sales", icon: "point_of_sale", roles: ["admin", "staff"] },
  { to: "/appointments", label: "Appointments", icon: "event", roles: ["admin", "staff"] },
  // Staff manage the client book and ring up sales, so these three are shared.
  { to: "/clients", label: "Clients", icon: "group", roles: ["admin", "staff"] },
  { to: "/admin/services", label: "Services", icon: "spa", roles: ["admin", "staff"] },
  { to: "/inventory", label: "Products", icon: "inventory_2", roles: ["admin", "staff"] },
  { to: "/employees", label: "Employees", icon: "badge", roles: ["admin"] },
  {
    to: "/staff",
    label: "Staff Management",
    icon: "manage_accounts",
    roles: ["admin"],
  },
  { to: "/payments", label: "Payments", icon: "payments", roles: ["admin"] },
  { to: "/expenses", label: "Expenses", icon: "receipt_long", roles: ["admin"] },
  { to: "/reports", label: "Reports", icon: "bar_chart", roles: ["admin"] },
  {
    to: "/my-attendance",
    label: "My Attendance & Leaves",
    icon: "fingerprint",
    roles: ["staff"],
  },
];

export default function SwastikSidebar({ onNavigate }) {
  const { user, signOut } = useAuth();
  // Least privilege: an unrecognised role falls back to staff, never admin.
  const role = normalizeRole(user);
  const items = NAV_ITEMS.filter((item) => !item.roles || item.roles.includes(role));

  return (
    <aside className="flex h-full w-72 flex-shrink-0 flex-col overflow-hidden bg-inverse-surface text-inverse-on-surface max-lg:w-64">
      <div className="flex-shrink-0 p-8 pb-5">
        <div className="flex items-center gap-3">
          <div className="gold-gradient flex h-10 w-10 items-center justify-center rounded-xl">
            <span className="material-symbols-outlined text-white" aria-hidden="true">
              spa
            </span>
          </div>
          <div>
            <h2 className="font-headline text-lg font-bold leading-none tracking-tight text-inverse-on-surface">
              Swastik Professional Salon
            </h2>
            <p className="mt-1 text-[10px] uppercase tracking-widest opacity-60">
              Premium Management
            </p>
          </div>
        </div>
      </div>

      <nav
        className="scrollbar-swastik min-h-0 flex-1 space-y-1 overflow-y-auto px-8 pb-5"
        aria-label="Main"
      >
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex min-h-[48px] items-center gap-3 rounded-xl border-l-4 px-4 py-3 text-sm font-medium transition-all duration-200 hover:translate-x-0.5 active:scale-[0.98] ${
                isActive
                  ? "border-primary-fixed bg-primary/10 text-primary-fixed"
                  : "border-transparent opacity-70 hover:bg-white/5 hover:opacity-100"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={`material-symbols-outlined ${
                    isActive ? "material-filled" : ""
                  }`}
                  aria-hidden="true"
                >
                  {item.icon}
                </span>
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="flex-shrink-0 border-t border-white/10 bg-inverse-surface p-8 pt-6">
        <div className="mb-5">
          <ButtonQuickAction role={role} />
        </div>
        <div className="px-2">
          <p className="truncate font-headline text-sm font-bold text-inverse-on-surface">
            {user?.name || "Guest"}
          </p>
          <p className="mt-0.5 truncate text-[10px] uppercase tracking-widest opacity-60">
            {titleCase(role)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => signOut()}
          className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-white/10 px-4 text-sm font-semibold text-inverse-on-surface transition-colors duration-200 hover:border-error/40 hover:bg-error/90 active:scale-[0.98]"
        >
          <span className="material-symbols-outlined text-lg" aria-hidden="true">
            logout
          </span>
          Sign Out
        </button>
      </div>
    </aside>
  );
}

function ButtonQuickAction({ role }) {
  const isStaff = role === "staff";
  return (
    <NavLink
      to={isStaff ? "/my-attendance" : "/appointments/new"}
      className="gold-gradient flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl py-3.5 font-headline text-sm font-bold text-white shadow-ambient transition-transform duration-200 hover:scale-[1.02] active:scale-95"
    >
      <span className="material-symbols-outlined text-lg" aria-hidden="true">
        {isStaff ? "fingerprint" : "add"}
      </span>
      {isStaff ? "Clock In / Out" : "New Appointment"}
    </NavLink>
  );
}
